import { useFormSchema } from "@portal/providers/FormSchemaProvider";
import type { FieldValues } from "react-hook-form";
import { useWatch } from "react-hook-form";
import {
	isEmpty,
	isFieldPartial,
	isFieldRequiredForApproval,
	isFieldRequiredForSubmit,
} from "../../utils/schema-fields";
import { PartialIndicator, Text } from "../primitives";
import { LineItem } from "../ui/LineItem";
import { RequiredForApprovalIndicator } from "../ui/RequiredForApprovalIndicator";
import { Tooltip } from "../ui/Tooltip";
import { FormField } from "./FormField";
import { FormLineItemProps } from "./types";

/**
 * Horizontal form layout: renders a label to the left of the input in a line-item row.
 * Used in table-style forms where fields are displayed as key-value pairs.
 */
export const FormLineItem = <TFieldValues extends FieldValues>({
	formMethods,
	id,
	name,
	label,
	tooltip,
	children,
	className,
	labelClassName,
	partialRequiresSubmit = true,
}: FormLineItemProps<TFieldValues> & { children: React.ReactNode }) => {
	const schema = useFormSchema();
	const fieldValue = useWatch({ control: formMethods.control, name });
	const { isSubmitted } = formMethods.formState;

	const shouldCheck = partialRequiresSubmit ? isSubmitted : true;
	const isPartial = shouldCheck && !!schema && isFieldPartial(schema, name) && isEmpty(fieldValue);
	const isRequiredForApproval =
		shouldCheck && !!schema && isFieldRequiredForApproval(schema, name) && isEmpty(fieldValue);
	const required = !!schema && isFieldRequiredForSubmit(schema, name);

	const showRequiredForApprovalIndicator = isRequiredForApproval;
	const showPartialIndicator = !showRequiredForApprovalIndicator && isPartial;

	return (
		<FormField id={id || name}>
			<LineItem
				label={
					<div className="flex items-center gap-x-[8px]">
						<span className={labelClassName}>
							{label}
							{required && <Text color="danger">{"\u00A0"}*</Text>}
						</span>
						{tooltip && <Tooltip id={`tooltip-${name}`} content={tooltip} className="w-[18px]" />}
						{showPartialIndicator && <PartialIndicator active={true} />}
						{showRequiredForApprovalIndicator && <RequiredForApprovalIndicator active={true} />}
					</div>
				}
				className={className}
			>
				{children}
			</LineItem>
		</FormField>
	);
};
